import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Star,
  Quote,
  Award,
  CheckCircle,
  Users,
  Cpu,
  Target,
  Shield,
  ArrowRight,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const TrustSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [autoPlay, setAutoPlay] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Client Logos - Replace with actual logo paths
  const clients = [
    {
      name: "L & T",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Mining Corporation Ltd",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Infrastructure Authority",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Urban Planning Council",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Tech Solutions Inc",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Architecture Associates",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Forest Conservation Org",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
    {
      name: "Government Projects",
      logo: "https://companieslogo.com/img/orig/LTTS.NS.D-e95344e3.png?t=1720244492",
    },
  ];

  // Testimonials
  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "Environmental Director",
      company: "Environmental Department",
      content:
        "Maptech's precision mapping and drone surveys transformed our forest conservation efforts. The data accuracy and detailed analysis exceeded our expectations.",
      rating: 5,
      project: "Forest Diversion Mapping",
    },
    {
      id: 2,
      name: "Rajesh Kumar",
      role: "Chief Technology Officer",
      company: "Mining Corporation Ltd",
      content:
        "The custom software solution streamlined our entire mining operation. Real-time monitoring and compliance tracking have been absolute game-changers for our business.",
      rating: 5,
      project: "Mining Management System",
    },
    {
      id: 3,
      name: "Emily Watson",
      role: "Senior Project Manager",
      company: "Infrastructure Authority",
      content:
        "Their environmental consulting expertise ensured our projects met all compliance standards while maintaining sustainability. Exceptional service and results.",
      rating: 5,
      project: "Environmental Consulting",
    },
  ];

  // Why Choose Us Features
  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Experienced Team",
      description:
        "15+ years of combined expertise in forest clearance and DGPS survey services",
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "Latest Technology",
      description:
        "Advanced DGPS equipment, drones, and GIS tools for precise surveying and mapping",
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Accurate Results",
      description:
        "High-precision mapping with 98%+ accuracy for forest clearance and land surveys",
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Trusted by Industry Leaders",
      description:
        "Preferred partner for 25+ leading organizations requiring forest clearance solutions",
    },
  ];

  // Auto-rotate testimonials
  React.useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoPlay, testimonials.length]);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    setAutoPlay(false);
  };

  const prevTestimonial = () => {
    setActiveTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
    setAutoPlay(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-6 lg:py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-brandBlue/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brandGreen/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Combined Testimonials & Why Choose Us */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* New Testimonial Design */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="flex items-center space-x-2 mb-8">
              <Quote className="w-6 h-6 text-brandGreen" />
              <h3 className="text-3xl lg:text-4xl font-bold text-white font-lexend">
                What Our Clients Say
              </h3>
            </div>

            <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-10">
              <div className="relative min-h-[300px]">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: 50, scale: 0.95 }}
                    animate={{
                      opacity: activeTestimonial === index ? 1 : 0,
                      x: activeTestimonial === index ? 0 : 50,
                      scale: activeTestimonial === index ? 1 : 0.95,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 25,
                      duration: 0.6,
                    }}
                    className={`absolute inset-0 p-2 ${
                      activeTestimonial === index ? "block" : "hidden"
                    }`}
                  >
                    {/* Rating Stars */}
                    <div className="flex space-x-1 mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>

                    {/* Testimonial Content */}
                    <div className="mb-8">
                      <p className="text-white/80 text-lg lg:text-xl leading-relaxed mb-4 italic">
                        "{testimonial.content}"
                      </p>
                      <div className="text-brandGreen text-sm font-semibold">
                        Project: {testimonial.project}
                      </div>
                    </div>

                    {/* Client Info */}
                    <div className="flex items-center space-x-4 p-4 rounded-2xl bg-white/5 border border-white/10">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brandBlue to-brandGreen flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {testimonial.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-bold text-lg">
                          {testimonial.name}
                        </div>
                        <div className="text-white/60 text-sm">
                          {testimonial.role}
                        </div>
                        <div className="text-brandGreen text-sm font-semibold">
                          {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Testimonial Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6">
                <div className="flex items-center space-x-4">
                  {/* Navigation Dots */}
                  <div className="flex space-x-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setActiveTestimonial(index);
                          setAutoPlay(false);
                        }}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          activeTestimonial === index
                            ? "bg-brandGreen scale-125"
                            : "bg-white/30 hover:bg-white/50"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Auto-play Toggle */}
                  <button
                    onClick={() => setAutoPlay(!autoPlay)}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300"
                  >
                    {autoPlay ? (
                      <Pause className="w-4 h-4 text-white" />
                    ) : (
                      <Play className="w-4 h-4 text-white" />
                    )}
                  </button>
                </div>

                {/* Arrow Navigation */}
                <div className="flex space-x-2">
                  <button
                    onClick={prevTestimonial}
                    className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 group"
                  >
                    <ChevronLeft className="w-5 h-5 text-white group-hover:text-brandGreen" />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 group"
                  >
                    <ChevronRight className="w-5 h-5 text-white group-hover:text-brandGreen" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="flex items-center space-x-2 mb-8">
              <Award className="w-6 h-6 text-brandBlue" />
              <h3 className="text-3xl lg:text-4xl font-bold text-white font-lexend">
                Why Choose Us
              </h3>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="space-y-4"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  variants={itemVariants}
                  className="group flex items-start space-x-4 p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-brandBlue to-brandGreen flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2 font-lexend">
                      {feature.title}
                    </h4>
                    <p className="text-white/70 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
